import { rrulestr } from 'rrule';
import { CalendarEvent as EngineEvent } from '@/types';

/**
 * Recurrence expansion for the calendar engine.
 *
 * Events carry an iCal/Google-style `recurrence: string[]` (RRULE/EXDATE lines; DTSTART is
 * implied by the event's own `start`). A "master" event (recurrence.length > 0) is NEVER
 * rendered directly — `expandRecurringEvents` turns it into virtual per-occurrence instances
 * for the visible date range, which then flow through the rest of CalendarEngine exactly like
 * any other event (timezone shift, midnight slicing, conflict detection, etc).
 *
 * A single occurrence can be "detached" from its master by materializing a real Firestore doc
 * with `recurringEventId` (master's id) + `originalStartTime` (the slot it replaces, on the
 * ORIGINAL unedited schedule). `expandRecurringEvents` skips synthesizing a virtual instance
 * for any slot that has a matching override doc in the event list — the override renders
 * normally instead, as a plain non-recurring event, wherever its own `start` now is.
 */

const ICAL_LINE_PREFIXES = ['RRULE:', 'EXRULE:', 'RDATE:', 'EXDATE:'];

// iCal UTC basic format: YYYYMMDDTHHMMSSZ
export function toIcalUtcString(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
}

function normalizeRecurrenceLines(lines: string[]): string[] {
  return lines.filter(Boolean).map((line) => {
    const trimmed = line.trim();
    const hasPrefix = ICAL_LINE_PREFIXES.some((p) => trimmed.toUpperCase().startsWith(p));
    return hasPrefix ? trimmed : `RRULE:${trimmed}`;
  });
}

export function expandRecurringEvents(
  events: EngineEvent[],
  rangeStart: Date,
  rangeEnd: Date
): EngineEvent[] {
  // Index detached single-occurrence overrides by `masterId__originalSlotISO` so we know
  // which slots to skip when synthesizing virtual instances for their master series.
  const overriddenSlots = new Set<string>();
  events.forEach((e) => {
    if (e.recurringEventId && e.originalStartTime?.dateTime) {
      overriddenSlots.add(`${e.recurringEventId}__${e.originalStartTime.dateTime}`);
    }
  });

  // Pad the window so occurrences starting just outside the visible range (but whose
  // multi-day slice could still touch it) aren't lost.
  const windowStart = new Date(rangeStart.getTime() - 24 * 60 * 60 * 1000);
  const windowEnd = new Date(rangeEnd.getTime() + 24 * 60 * 60 * 1000);

  const result: EngineEvent[] = [];

  events.forEach((event) => {
    if (!event.recurrence || event.recurrence.length === 0) {
      result.push(event);
      return;
    }

    const baseStart = new Date(event.start);
    const baseEnd = new Date(event.end);
    if (isNaN(baseStart.getTime()) || isNaN(baseEnd.getTime())) {
      result.push(event); // malformed — render as a single event rather than dropping it
      return;
    }
    const durationMs = baseEnd.getTime() - baseStart.getTime();

    try {
      const lines = normalizeRecurrenceLines(event.recurrence);
      const dtstartLine = `DTSTART:${toIcalUtcString(baseStart)}`;
      const ruleSet = rrulestr([dtstartLine, ...lines].join('\n'));
      const occurrences: Date[] = ruleSet.between(windowStart, windowEnd, true);

      occurrences.forEach((occStart) => {
        const occStartISO = occStart.toISOString();
        if (overriddenSlots.has(`${event.id}__${occStartISO}`)) return;

        const occEnd = new Date(occStart.getTime() + durationMs);
        result.push({
          ...event,
          id: `${event.id}_occ_${occStartISO}`,
          start: occStartISO,
          end: occEnd.toISOString(),
          seriesId: event.id,
          isRecurrenceInstance: true,
          // Lets the save/delete handlers find this exact slot again without string-parsing
          // the synthetic id — same field materialized overrides use for correlation.
          originalStartTime: { dateTime: occStartISO, timeZone: 'UTC' },
          recurrence: undefined, // virtual instances aren't themselves recurring masters
        });
      });
    } catch (err) {
      console.error(`[recurrence] Failed to expand event ${event.id}:`, err);
      result.push(event); // fail open — show the single original event rather than nothing
    }
  });

  return result;
}

/** Append an EXDATE for one occurrence — used when deleting "just this instance". */
export function addExdateToRecurrence(recurrence: string[], occurrenceStart: Date): string[] {
  const lines = normalizeRecurrenceLines(recurrence);
  return [...lines, `EXDATE:${toIcalUtcString(occurrenceStart)}`];
}

/**
 * Clamp a master's RRULE to end just before `boundaryStart` — used for "this and following"
 * edits/deletes to end the old series right before the target occurrence. Any existing
 * UNTIL/COUNT is replaced (they're mutually exclusive with a fresh UNTIL anyway).
 */
export function clampRecurrenceUntil(recurrence: string[], boundaryStart: Date): string[] {
  const lines = normalizeRecurrenceLines(recurrence);
  const untilDate = new Date(boundaryStart.getTime() - 1000); // 1s before, RRULE UNTIL is inclusive
  const untilStr = toIcalUtcString(untilDate);
  return lines.map((line) => {
    if (!line.toUpperCase().startsWith('RRULE:')) return line;
    const withoutBound = line.replace(/;?(UNTIL|COUNT)=[^;]*/gi, '');
    return `${withoutBound};UNTIL=${untilStr}`;
  });
}

/** Strip UNTIL/COUNT so a new series split off via "this and following" continues indefinitely. */
export function stripRecurrenceBounds(recurrence: string[]): string[] {
  const lines = normalizeRecurrenceLines(recurrence);
  return lines.map((line) =>
    line.toUpperCase().startsWith('RRULE:') ? line.replace(/;?(UNTIL|COUNT)=[^;]*/gi, '') : line
  );
}
